import { Injectable } from '@angular/core';
import {
    FlightConformanceResultDto,
    FlyFreelyError,
    GqlFilterField,
    MissionRoleDto,
    MissionService,
    MissionSortFieldName,
    MissionSortFields,
    MissionSummary,
    MissionSummaryDto,
    PersonDto,
    validatedSortEntryArray,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import moment, { Moment } from 'moment';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

export interface OutstandingStatusMission {
    id: number;
    name: string;
    rpic: PersonDto;
    missionDate: string;
    timeZone: string;
    convertedMissionDate: Moment;
    locationName: string;
    rpaNicknames: string;
    workflowName: string;
}

interface OutstandingMissionSearchCriteria {
    page: number;
    limit: number;
    sorting: ColumnSortPreferences;
    filters: GqlFilterField[];
    organisationId: number;
    conformanceResultStatus: FlightConformanceResultDto.Status[];
    authorityIds?: number[];
    orOrganisationId?: number;
    status?: MissionSummaryDto.Status[];
    outstandingApprovals?: boolean;
    readyForFinalisation?: boolean;
}

@Injectable()
export class MissionOutstandingStatusDataService {
    private workingSource = new ReplaySubject<boolean>(1);
    private missionsSource = new ReplaySubject<OutstandingStatusMission[]>(1);
    private currentPageSource = new BehaviorSubject<number>(0);
    private totalItemsSource = new ReplaySubject<number>(1);

    private searchCriteriaSubject =
        new ReplaySubject<OutstandingMissionSearchCriteria>(1);

    searchCriteria$ = this.searchCriteriaSubject.asObservable();

    working$ = this.workingSource.asObservable();
    missions$ = this.missionsSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(private missionService: MissionService) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.searchCriteriaSubject.subscribe(criteria =>
            this.updateMissionTable(criteria)
        );
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.missionsSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.searchCriteriaSubject.complete();
    }

    findMissions(
        page: number,
        limit: number,
        sorting: ColumnSortPreferences,
        organisationId: number,
        requiredStatus: 'approval' | 'finalisation',
        authorityIds?: number[],
        orOrganisationId?: number
    ) {
        const filters: GqlFilterField[] =
            requiredStatus === 'approval'
                ? [
                      { field: 'isDummy', filter: 'false' },
                      { field: 'outstandingApprovals', filter: 'true' }
                  ]
                : [
                      { field: 'isDummy', filter: 'false' },
                      { field: 'readyForFinalisation', filter: 'true' }
                  ];
        const status: MissionSummaryDto.Status[] =
            requiredStatus === 'approval'
                ? [MissionSummaryDto.Status.DRAFT]
                : [MissionSummaryDto.Status.COMPLETED];
        const conformanceResultStatus: FlightConformanceResultDto.Status[] =
            null;
        const outstandingApprovals = requiredStatus === 'approval' || null;
        const readyForFinalisation = requiredStatus === 'finalisation' || null;
        this.searchCriteriaSubject.next({
            page,
            limit,
            sorting,
            filters,
            organisationId,
            conformanceResultStatus,
            authorityIds,
            orOrganisationId,
            status,
            outstandingApprovals,
            readyForFinalisation
        });
    }

    private updateMissionTable({
        page,
        limit,
        sorting,
        filters,
        organisationId,
        authorityIds,
        orOrganisationId,
        status,
        conformanceResultStatus,
        outstandingApprovals,
        readyForFinalisation
    }: OutstandingMissionSearchCriteria) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const doneWorking = this.workTracker.createTracker();

        // this.subscriptionTypes$ is used here to ensure it has a value and prevent a race condition.
        const sortFields = validatedSortEntryArray(
            this.parseFilterFieldNames(
                sorting?.column ?? null
            ) as MissionSortFieldName,
            sorting?.ascending,
            MissionSortFields
        );

        this.missionService
            .findMissionSummaries(
                {
                    organisationId,
                    conformanceResultStatus,
                    authorityIds,
                    orOrganisationId,
                    status,
                    outstandingApprovals,
                    readyForFinalisation
                },
                {
                    page: page,
                    pageSize: limit,
                    sortFields
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$), finalize(doneWorking))
            .subscribe({
                next: missions => {
                    const findRpic = (mission: MissionSummary) => {
                        return mission.missionCrew?.find(
                            p =>
                                p.role.coreRole ===
                                MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                        )?.person as PersonDto | undefined;
                    };
                    const findRpaNames = (mission: MissionSummary) => {
                        const names = (mission.rpaLinkList ?? [])
                            .map((c, i) =>
                                i < 3 ? c.rpa.nickname : i === 3 ? '...' : null
                            )
                            .filter(c => c != null);
                        return names.join(', ');
                    };
                    const missionDetails: OutstandingStatusMission[] =
                        missions.results.map((r, i) => ({
                            id: r.id,
                            name: r.name,
                            rpic: findRpic(r),
                            missionDate: r.missionDate,
                            timeZone: r.timeZone,
                            convertedMissionDate: moment(r.missionDate).tz(
                                r.timeZone
                            ),
                            locationName: r.location?.name,
                            rpaNicknames: findRpaNames(r),
                            workflowName: r.workflowVersion?.workflow?.name
                        }));
                    this.totalItemsSource.next(missions.count);
                    this.currentPageSource.next(page);
                    this.missionsSource.next(missionDetails);
                },
                error: (error: FlyFreelyError) => {
                    console.error(error);
                    this.missionsSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                }
            });
    }

    parseFilterFieldNames(name: string) {
        switch (name) {
            case 'missionCrewNames':
                return 'missionCrew';

            case 'missionType.name':
                return 'missionOperationType';

            case 'convertedMissionDate':
                return 'missionDate';

            default:
                return name;
        }
    }
}
